import React, { useCallback } from "react";
import { Button, Slider, MenuItem, Select, FormControl, InputLabel, Alert, FormControlLabel, Switch } from "@mui/material";
import Icon from "@mdi/react";
import { mdiAccountVoice, mdiPlayCircleOutline, mdiUndoVariant } from "@mdi/js";
import rootStyles from "../styles.module.scss";
import styles from "./styles.module.scss";
import { useVoicePreferences } from "shared/contexts";
import { useTTS, isTTSSupported } from "shared/hooks";

const SAMPLE_TEXT = "Olá! Sou o Líder da sua equipe. Assim que você falar, eu coordeno os membros para executar a tarefa.";

export const VoicePage = () => {
	const { rate, pitch, volume, voiceURI, bargeIn, setRate, setPitch, setVolume, setVoiceURI, setBargeIn, reset } = useVoicePreferences();
	const tts = useTTS({ rate, pitch, volume, voiceURI });

	const handleTest = useCallback(() => {
		tts.speak(SAMPLE_TEXT);
	}, [tts]);

	const ttsSupported = isTTSSupported();

	return (
		<div className={rootStyles.contentWrapper}>
			<section className={rootStyles.section}>
				<div className={rootStyles.sectionHeader}>
					<div className={rootStyles.icon}>
						<Icon
							path={mdiAccountVoice}
							size={1.2}
						/>
					</div>
					<div className={rootStyles.titles}>
						<h3>Conversa por Voz</h3>
						<p>Ajuste como o Líder fala no modo de conversa por voz (Web Speech API).</p>
					</div>
				</div>

				{!ttsSupported && (
					<Alert
						severity="warning"
						className={styles.alert}
					>
						Seu navegador não suporta síntese de voz. Use uma versão recente do Chrome, Edge ou Safari.
					</Alert>
				)}

				<div className={rootStyles.card}>
					<FormControl
						fullWidth
						size="small"
						className={styles.field}
					>
						<InputLabel id="voice-select-label">Voz</InputLabel>
						<Select
							labelId="voice-select-label"
							label="Voz"
							value={tts.voices.some((v) => v.voiceURI === voiceURI) ? voiceURI ?? "" : ""}
							onChange={(e) => setVoiceURI(e.target.value || null)}
							displayEmpty
						>
							<MenuItem value="">Voz padrão do sistema (pt-BR)</MenuItem>
							{tts.voices.map((v) => (
								<MenuItem
									key={v.voiceURI}
									value={v.voiceURI}
								>
									{v.name} ({v.lang})
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<div className={styles.sliderRow}>
						<label className={styles.sliderLabel}>
							Velocidade <span className={styles.sliderValue}>{rate.toFixed(1)}x</span>
						</label>
						<Slider
							value={rate}
							min={0.5}
							max={2}
							step={0.1}
							marks
							onChange={(_, v) => setRate(v as number)}
							valueLabelDisplay="auto"
						/>
					</div>

					<div className={styles.sliderRow}>
						<label className={styles.sliderLabel}>
							Tom <span className={styles.sliderValue}>{pitch.toFixed(1)}</span>
						</label>
						<Slider
							value={pitch}
							min={0}
							max={2}
							step={0.1}
							marks
							onChange={(_, v) => setPitch(v as number)}
							valueLabelDisplay="auto"
						/>
					</div>

					<div className={styles.sliderRow}>
						<label className={styles.sliderLabel}>
							Volume <span className={styles.sliderValue}>{Math.round(volume * 100)}%</span>
						</label>
						<Slider
							value={volume}
							min={0}
							max={1}
							step={0.05}
							onChange={(_, v) => setVolume(v as number)}
							valueLabelDisplay="auto"
							valueLabelFormat={(v) => `${Math.round((v as number) * 100)}%`}
						/>
					</div>

					<div className={styles.sliderRow}>
						<FormControlLabel
							control={
								<Switch
									checked={bargeIn}
									onChange={(_, checked) => setBargeIn(checked)}
								/>
							}
							label="Permitir interromper o Líder falando por cima (barge-in)"
						/>
						<p className={styles.hint}>
							Quando ativo, falar enquanto o Líder responde encerra a fala dele e abre o microfone.
							Pode disparar interrupções acidentais por causa do som do alto-falante.
							Desativado garante que toda a resposta seja ouvida até o fim.
						</p>
					</div>

					<div className={styles.actions}>
						<Button
							variant="contained"
							startIcon={
								<Icon
									path={mdiPlayCircleOutline}
									size={0.8}
								/>
							}
							onClick={handleTest}
							disabled={!ttsSupported || tts.speaking}
						>
							{tts.speaking ? "Reproduzindo..." : "Testar voz"}
						</Button>
						<Button
							variant="text"
							startIcon={
								<Icon
									path={mdiUndoVariant}
									size={0.8}
								/>
							}
							onClick={reset}
						>
							Restaurar padrão
						</Button>
					</div>
				</div>
			</section>
		</div>
	);
};
